// src/components/PIIAnonymizerPage/PIIFileList.jsx
import React from 'react';
import {
    Box,
    Stack,
    Typography,
    Chip,
    IconButton,
    LinearProgress,
    Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';

const isPdfFile = (fileItem) => (fileItem.downloadName || fileItem.name || '').toLowerCase().endsWith('.pdf');
const isDocxFile = (fileItem) => (fileItem.downloadName || fileItem.name || '').toLowerCase().endsWith('.docx');

const PIIFileList = ({
    files,
    isUploading,
    statusColor,
    statusLabel,
    onRemove,
    onDownload,
    onPreview, 
}) => {
    if (!files || files.length === 0) return null;

    return (
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {files.map((f) => (
                <Box
                    key={f.id}
                    sx={(theme) => ({
                        p: 2,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            borderColor: 'primary.main',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
                        }
                    })}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ overflow: 'hidden', mr: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 2, bgcolor: 'action.selected', color: 'text.secondary' }}>
                                <InsertDriveFileOutlinedIcon fontSize="small" />
                            </Box>
                            <Box sx={{ overflow: 'hidden' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {f.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {(f.size / 1024 / 1024).toFixed(2)} MB
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                            <Chip label={statusLabel(f.status)} color={statusColor(f.status)} size="small" variant="outlined" sx={{ borderRadius: '6px', fontWeight: 600, fontSize: '0.75rem' }} />

                            {(f.status === 'success' || f.status === 'queued') && (
                                <>
                                    {(isPdfFile(f) || isDocxFile(f)) && (
                                        <Tooltip title="Náhľad / Úprava">
                                            <IconButton size="small" onClick={() => onPreview(f)} sx={(theme) => ({ color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) })}>
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Stiahnuť">
                                        <IconButton size="small" onClick={() => onDownload(f)} sx={{ color: 'text.secondary' }}>
                                            <DownloadIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}

                            <Tooltip title="Odstrániť">
                                <span>
                                    <IconButton size="small" onClick={() => onRemove(f.id)} disabled={isUploading} sx={(theme) => ({ color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.1) } })}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    </Stack>

                    <LinearProgress
                        variant="determinate"
                        value={f.progress}
                        sx={(theme) => ({
                            height: 6,
                            borderRadius: 3,
                            bgcolor: 'action.selected',
                            '& .MuiLinearProgress-bar': { borderRadius: 3 }
                        })}
                    />

                    {f.error && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', fontWeight: 500 }}>
                            {f.error}
                        </Typography>
                    )}
                </Box>
            ))}
        </Box>
    );
};

export default PIIFileList;